import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Empty, Input, Popconfirm, Select, Skeleton, Space, Statistic,
  Table, Tag, Typography, message,
} from 'antd';
import { PhoneField, TextField } from '../../ui/fields';
import { Field, FormModal, FormSection, V } from '../../ui/forms';
import { LoginOutlined, LogoutOutlined } from '@ant-design/icons';
import type { CheckInVisitorRequest, VisitorPurpose, VisitorRow } from '@velora/contracts';
import { productApi } from '../../api';

/**
 * Front desk — Visitors.
 * Today's log with inside-now count + quick check-in + one-tap check-out.
 */
const PURPOSE_LABELS: Record<VisitorPurpose, string> = {
  parent_meeting: 'Parent meeting', vendor: 'Vendor', interview: 'Interview',
  audit: 'Audit', other: 'Other',
};

export function VisitorsPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);

  const list = useQuery({
    queryKey: ['visitors', institution],
    queryFn: () => productApi.listVisitors(institution),
    enabled: !!institution,
  });
  const checkout = useMutation({
    mutationFn: (id: string) => productApi.checkOutVisitor(institution, id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['visitors', institution] }); void message.success('Checked out'); },
  });

  const rows = list.data?.rows ?? [];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>Visitors</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            Today's log. {list.data?.inside_now ?? 0} inside now.
          </Typography.Text>
        </div>
        <Button type="primary" icon={<LoginOutlined />} onClick={() => setAddOpen(true)}>Check in</Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
        <Card size="small"><Statistic title="Today total" value={rows.length} /></Card>
        <Card size="small"><Statistic title="Inside now" value={list.data?.inside_now ?? 0} valueStyle={{ color: (list.data?.inside_now ?? 0) > 0 ? '#2f6ab0' : undefined }} /></Card>
        <Card size="small"><Statistic title="Checked out" value={rows.length - (list.data?.inside_now ?? 0)} /></Card>
      </div>

      <Card size="small" title={`${rows.length} visitors today`}>
        {list.isLoading && <Skeleton active paragraph={{ rows: 4 }} />}
        {rows.length === 0 && !list.isLoading && (
          <Empty description={<span>No visitors yet today. <a onClick={() => setAddOpen(true)}>Check in the first</a>.</span>} style={{ padding: 20 }} />
        )}
        {rows.length > 0 && (
          <Table<VisitorRow>
            size="small"
            pagination={false}
            rowKey="id"
            dataSource={rows}
            columns={[
              { title: 'Name', dataIndex: 'name', key: 'n', render: (v: string) => <b>{v}</b> },
              { title: 'Phone', dataIndex: 'phone', key: 'p', width: 140, render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
              { title: 'Purpose', dataIndex: 'purpose', key: 'pu', width: 130, render: (v: VisitorPurpose) => <Tag>{PURPOSE_LABELS[v]}</Tag> },
              { title: 'Meeting with', dataIndex: 'meeting_with', key: 'm', render: (v: string | null) => v ?? <Typography.Text type="secondary">—</Typography.Text> },
              { title: 'Badge', dataIndex: 'badge_no', key: 'b', width: 80, render: (v: string | null) => v ? <Tag>{v}</Tag> : <Typography.Text type="secondary">—</Typography.Text> },
              { title: 'In', dataIndex: 'in_at', key: 'i', width: 110, render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{fmtTime(v)}</span> },
              { title: 'Out', dataIndex: 'out_at', key: 'o', width: 110, render: (v: string | null) => v ? <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{fmtTime(v)}</span> : <Tag color="processing">inside</Tag> },
              {
                key: 'a', width: 130, align: 'right',
                render: (_, r) => r.out_at ? null : (
                  <Popconfirm title="Check out this visitor?" onConfirm={() => checkout.mutate(r.id)} okText="Check out">
                    <Button size="small" icon={<LogoutOutlined />}>Out</Button>
                  </Popconfirm>
                ),
              },
            ]}
          />
        )}
      </Card>

      <CheckInModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        institution={institution}
        onCreated={() => {
          setAddOpen(false);
          void qc.invalidateQueries({ queryKey: ['visitors', institution] });
          void message.success('Checked in');
        }}
      />
    </Space>
  );
}

function CheckInModal({
  open, onClose, institution, onCreated,
}: { open: boolean; onClose: () => void; institution: string; onCreated: () => void }) {
  return (
    <FormModal<CheckInVisitorRequest>
      open={open} onClose={onClose}
      title="Check in" submitText="Check in"
      initial={{ purpose: 'parent_meeting' }}
      mutation={(v) => productApi.checkInVisitor(institution, {
        name: v.name, phone: v.phone,
        purpose: v.purpose,
        meeting_with: v.meeting_with || undefined,
        badge_no: v.badge_no || undefined,
        note: v.note?.trim() || undefined,
      })}
      onSuccess={onCreated}
    >
      <FormSection cols={2}>
        <Field name="name" label="Name" required><TextField autoFocus /></Field>
        <Field name="phone" label="Phone" required rules={[V.phone()]}><PhoneField /></Field>
        <Field name="purpose" label="Purpose" required>
          <Select options={(Object.keys(PURPOSE_LABELS) as VisitorPurpose[]).map((p) => ({ value: p, label: PURPOSE_LABELS[p] }))} />
        </Field>
        <Field name="badge_no" label="Badge #">
          <TextField placeholder="V-014" />
        </Field>
      </FormSection>
      <Field name="meeting_with" label="Meeting with">
        <TextField placeholder="Principal / A. Sharma / staffroom" />
      </Field>
      <Field name="note" label="Note">
        <Input.TextArea rows={2} />
      </Field>
    </FormModal>
  );
}

function fmtTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}
